import { CommonBookingInfoViewHeader } from '@kiwicom/openapi-sdk/mmb';
import Stack from '@kiwicom/orbit-components/lib/Stack';
import * as React from 'react';
import Skeleton from '@kiwicom/orbit-components/lib/Skeleton';
import Heading from '@kiwicom/orbit-components/lib/Heading';
import { Category, createEventLogUnified, UIAction } from '@/common/services/logging';
import { useLog } from '@kiwicom/nitro/lib/services/log/context';
import { useRouter } from 'next/router';

import ItineraryStops from './components/ItineraryStops';
import BreadcrumbNavigationToMmb from '../BreadcrumbNavigationToMmb/BreadcrumbNavigationToMmb';
import PrimitivePageSection from '../Layout/components/PrimitivePageSection';
import PageMeta from './components/PageMeta';
import { PAGE_HEADER_TITLE_HEIGHT } from '../Loadable/consts';
import useBid from '../../services/hooks/useBid';
import BreadcrumbBackwardNavigation from '../BreadcrumbBackwardNavigation/BreadcrumbBackwardNavigation';
import BackButton from '../BackButton';
import { useOptionalPathToMmb } from '../../services/hooks/usePathToMmb';
// eslint-disable-next-line import/no-restricted-paths
import useTravelCompanionPaths from '@/modules/TravelCompanion/services/useTravelCompanionPaths';

type Props = {
  bookingInfo?: CommonBookingInfoViewHeader | null;
  hasBackButton?: boolean;
  isLoadingBookingInfo?: boolean;
  title: string;
  isLoadingTitle?: boolean; // only for cases when title is dynamic on some BFF data
  description?: React.ReactNode;
  metaDescription?: string;
  alert?: React.ReactNode;
  illustration?: React.ReactNode;
  dataTest?: string;
};

/**
 * Description of the component and its props can be found on the link:
 * {@link https://kiwi.wiki/mmb/web-client/layout/#pageheader}.
 *
 * @returns header component for all pages
 */
const PageHeader = ({
  dataTest,
  title,
  isLoadingTitle = false,
  description,
  metaDescription,
  alert,
  bookingInfo,
  // Display back to Live Boarding Pass button.
  hasBackButton = false,
  isLoadingBookingInfo = false,
  illustration,
}: Props) => {
  const { query } = useRouter();
  const bid = useBid();
  const { log } = useLog();
  const pathToMmb = useOptionalPathToMmb();
  const { details: pathToTravelCompanion } = useTravelCompanionPaths();

  const BreadcrumbNavigation = bid ? BreadcrumbNavigationToMmb : BreadcrumbBackwardNavigation;

  if (hasBackButton && !bid) {
    throw new Error('Back button is only supported with a BID');
  }

  return (
    <>
      <PageMeta title={title} description={metaDescription} />
      <PrimitivePageSection>
        <Stack dataTest="page header">
          <Stack direction="column" spacing="400">
            {!hasBackButton && <BreadcrumbNavigation />}
            <div>
              {illustration}
              {isLoadingBookingInfo ? (
                // TODO MMB-4683: tkey for title
                <Skeleton width={150} height={24} title="Loading booking info" />
              ) : (
                bookingInfo && (
                  <ItineraryStops
                    stops={bookingInfo.displayStops}
                    itineraryType={bookingInfo.itineraryType}
                  />
                )
              )}
              <Stack direction="row" spacing="300">
                {hasBackButton && (
                  <BackButton
                    // tripId is used to determine if a user has Kiwi.com guarantee even if the tripId value is 'undefined'
                    // Only Kiwi.com guarantee users should be redirected to Travel Companion
                    fallbackUrl={query.tripId ? pathToTravelCompanion : pathToMmb}
                    hasShadow
                    onClick={() =>
                      log(
                        createEventLogUnified(
                          Category.COMMON,
                          UIAction.ButtonClicked,
                          'page header back',
                        ),
                        { browserHistoryLength: window.history.length },
                      ).catch()
                    }
                  />
                )}
                <Heading dataTest={dataTest} as="h1" type="title1" tablet={{ type: 'title0' }}>
                  {isLoadingTitle ? (
                    <Skeleton
                      width="100%"
                      height={PAGE_HEADER_TITLE_HEIGHT}
                      title="Loading title" // TODO MMB-4683: tkey for title
                    />
                  ) : (
                    title
                  )}
                </Heading>
              </Stack>
            </div>
          </Stack>
          {description}
          {alert}
        </Stack>
      </PrimitivePageSection>
    </>
  );
};

export default PageHeader;
